import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Descriptions,
  Modal,
  Result,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import B2BLayout from '../../layouts/b2b'
import {
  activateDomain,
  checkDomain,
  deactivateDomain,
  deleteTenant,
  getTenantById,
  runProvisionJob,
} from './api'
import TenantSubscriptionCard from './components/TenantSubscriptionCard'
import { sslPresentation } from './ssl-status'

const tenantStatusMap: Record<string, { label: string; color: string }> = {
  active: { label: 'Активен', color: 'green' },
  inactive: { label: 'Неактивен', color: 'default' },
  suspended: { label: 'Заблокирован', color: 'red' },
  pending: { label: 'Ожидание', color: 'orange' },
}

const domainStatusMap: Record<string, { label: string; color: string }> = {
  active: { label: 'Активен', color: 'green' },
  reserved: { label: 'Зарезервирован', color: 'orange' },
  inactive: { label: 'Неактивен', color: 'default' },
  deactivated: { label: 'Деактивирован', color: 'default' },
  pending: { label: 'Ожидание', color: 'blue' },
}

const dataSourceStatusMap: Record<string, { label: string; color: string }> = {
  active: { label: 'Активна', color: 'green' },
  inactive: { label: 'Неактивна', color: 'default' },
  provisioning: { label: 'Создаётся', color: 'blue' },
  failed: { label: 'Ошибка', color: 'red' },
}

const pipelineStatusMap: Record<string, { label: string; color: string }> = {
  done: { label: 'Готов', color: 'green' },
  failed: { label: 'Ошибка', color: 'red' },
  running: { label: 'Выполняется', color: 'blue' },
  pending: { label: 'Ожидание', color: 'orange' },
  created: { label: 'Создан', color: 'default' },
}

const stepTitleMap: Record<string, string> = {
  init: 'Инициализация',
  domain_reserve: 'Регистрация домена',
  db_provision: 'Создание БД/пользователя',
  migrate: 'Миграции',
  activate: 'Активация домена/подписки',
}

function fmt(val: any) {
  return val ? String(val) : '—'
}

function fmtDate(val: any) {
  if (!val) return '—'
  return new Date(val).toLocaleString('ru-RU')
}

export default function AdminTenantCardPage() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<any>(null)
  const [runningJobId, setRunningJobId] = useState<string | null>(null)
  const [checkModal, setCheckModal] = useState<any>({ open: false })
  const [errorModal, setErrorModal] = useState<{ open: boolean; text: string }>({
    open: false,
    text: '',
  })
  const [deleteModal, setDeleteModal] = useState(false)

  const load = useCallback(async () => {
    if (!tenantId) return
    try {
      const result = await getTenantById(tenantId)
      setInfo(result?.data || null)
      setError(null)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    load()
  }, [load])

  const latestJob = info?.jobs?.[0] || null

  const onRun = useCallback(
    async (force?: boolean) => {
      const jobId = String(latestJob?.id || '')
      if (!jobId) return
      setRunningJobId(jobId)
      try {
        const result = await runProvisionJob(jobId, force ? { force: true } : {})
        if (result?.errors?.length) {
          const msg = result.errors
            .map((e: any) => String(e?.detail || e?.message || ''))
            .join('. ')
          setErrorModal({ open: true, text: msg })
        }
        await load()
      } finally {
        setRunningJobId(null)
      }
    },
    [latestJob, load],
  )

  const onCheck = useCallback(async () => {
    const hostname = String(info?.domain?.hostname || '')
    if (!hostname) return
    const result = await checkDomain(hostname)
    const d = result?.data || {}
    setCheckModal({
      open: true,
      hostname,
      dnsOk: d.dns?.ok || false,
      dnsAddresses: d.dns?.addresses || [],
      httpsOk: d.https?.ok || false,
      httpsError: d.https?.error || null,
      tls: d.https?.tls || null,
    })
  }, [info])

  const onToggleActive = useCallback(async () => {
    const hostname = String(info?.domain?.hostname || '')
    if (!hostname) return
    const isActive = String(info?.domain?.status || '') === 'active'
    try {
      if (isActive) await deactivateDomain(hostname)
      else await activateDomain(hostname)
      await load()
    } catch (e) {
      setErrorModal({ open: true, text: String(e) })
    }
  }, [info, load])

  const onDelete = useCallback(async () => {
    const hostname = String(info?.domain?.hostname || '')
    if (!hostname) return
    try {
      await deleteTenant(hostname)
      navigate('/admin/dealers/domains')
    } catch (e) {
      setErrorModal({ open: true, text: String(e) })
    }
    setDeleteModal(false)
  }, [info, navigate])

  const breadcrumbs = [
    { href: '/admin/dealers/domains', title: 'Поддомены дилеров' },
    {
      href: `/admin/dealers/domains/${tenantId}`,
      title: info?.tenant?.name || tenantId || 'Тенант',
    },
  ]

  if (loading)
    return (
      <B2BLayout breadcrumbs={breadcrumbs}>
        <Skeleton active />
      </B2BLayout>
    )
  if (error)
    return (
      <B2BLayout breadcrumbs={breadcrumbs}>
        <Result status="error" title="Ошибка" subTitle={error} />
      </B2BLayout>
    )
  if (!info)
    return (
      <B2BLayout breadcrumbs={breadcrumbs}>
        <Result status="404" title="Тенант не найден" />
      </B2BLayout>
    )

  const { tenant, domain, data_source, subscription, billing, jobs, manager_name, created_by } =
    info
  const domainStatus = domainStatusMap[String(domain?.status || '')]
  const dataSourceStatus = dataSourceStatusMap[String(data_source?.status || '')]
  const hostname = domain?.hostname || null
  const cabinetUrl = hostname ? `https://${hostname}` : null
  const jobStatus = pipelineStatusMap[String(latestJob?.status || '')]
  const isDone = latestJob?.status === 'done'
  const ssl = sslPresentation(checkModal.tls)

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={tenant?.name || 'Тенант'}>
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <Card title="Общая информация">
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Название">{fmt(tenant?.name)}</Descriptions.Item>
            <Descriptions.Item label="Slug">{fmt(tenant?.slug)}</Descriptions.Item>
            <Descriptions.Item label="Код дилера">{fmt(tenant?.dealer_code)}</Descriptions.Item>
            <Descriptions.Item label="Статус тенанта">
              {(() => {
                const s = tenantStatusMap[String(tenant?.status || '')]
                return <Tag color={s?.color ?? 'default'}>{s?.label ?? fmt(tenant?.status)}</Tag>
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="Кем создан">
              {created_by == null ? (
                <Typography.Text type="secondary">Самостоятельно</Typography.Text>
              ) : (
                manager_name || `ID: ${created_by}`
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Создан">{fmtDate(tenant?.created_at)}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          title="Домен"
          extra={
            <Space>
              <Button size="small" onClick={onCheck} disabled={!hostname}>
                Проверить DNS/SSL
              </Button>
              <Button size="small" onClick={onToggleActive} disabled={!hostname}>
                {domain?.status === 'active' ? 'Деактивировать' : 'Активировать'}
              </Button>
            </Space>
          }
        >
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Hostname">
              {hostname ? <Typography.Text copyable>{hostname}</Typography.Text> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Статус">
              {domain ? (
                <Tag color={domainStatus?.color}>{domainStatus?.label || domain.status}</Tag>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Поддомен">{fmt(domain?.subdomain_label)}</Descriptions.Item>
            <Descriptions.Item label="Базовый домен">{fmt(domain?.base_domain)}</Descriptions.Item>
            <Descriptions.Item label="Ссылка на кабинет" span={2}>
              {cabinetUrl ? (
                <Typography.Text copyable={{ text: cabinetUrl }}>
                  {cabinetUrl}
                </Typography.Text>
              ) : (
                '—'
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="База данных">
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Host:Port">
              {data_source ? `${data_source.db_host}:${data_source.db_port || 5432}` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="БД">{fmt(data_source?.db_name)}</Descriptions.Item>
            <Descriptions.Item label="Пользователь">{fmt(data_source?.db_user)}</Descriptions.Item>
            <Descriptions.Item label="Статус">
              {data_source ? (
                <Tag color={dataSourceStatus?.color ?? 'default'}>
                  {dataSourceStatus?.label || fmt(data_source.status)}
                </Tag>
              ) : (
                '—'
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Подписка">
          <TenantSubscriptionCard
            tenantId={String(tenantId)}
            subscription={subscription}
            billing={billing}
            onReload={load}
          />
        </Card>

        <Card
          title="Пайплайн (последний job)"
          extra={
            <Space>
              {!isDone && (
                <>
                  <Button
                    size="small"
                    type="primary"
                    loading={runningJobId === latestJob?.id}
                    onClick={() => onRun()}
                    disabled={!latestJob}
                  >
                    Запустить
                  </Button>
                  <Button
                    size="small"
                    loading={runningJobId === latestJob?.id}
                    onClick={() => onRun(true)}
                    disabled={!latestJob}
                  >
                    Force
                  </Button>
                </>
              )}
            </Space>
          }
        >
          {latestJob ? (
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Статус">
                <Tag color={jobStatus?.color}>{jobStatus?.label || latestJob.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Шаг">
                {stepTitleMap[latestJob.step] || latestJob.step || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Попытки">{fmt(latestJob.attempts)}</Descriptions.Item>
              <Descriptions.Item label="Обновлён">
                {fmtDate(latestJob.updated_at)}
              </Descriptions.Item>
              {latestJob.error_message && (
                <Descriptions.Item label="Ошибка" span={2}>
                  <Typography.Text type="danger">{latestJob.error_message}</Typography.Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          ) : (
            <Typography.Text type="secondary">Нет provisioning job</Typography.Text>
          )}
        </Card>

        <Card title="История jobs">
          <Table
            rowKey="id"
            size="small"
            dataSource={jobs}
            pagination={false}
            columns={[
              {
                title: 'Статус',
                key: 'status',
                render: (_, row: any) => {
                  const s = pipelineStatusMap[row.status]
                  return <Tag color={s?.color}>{s?.label || row.status}</Tag>
                },
              },
              {
                title: 'Шаг',
                key: 'step',
                render: (_, row: any) => stepTitleMap[row.step] || row.step || '—',
              },
              { title: 'Попытки', dataIndex: 'attempts', key: 'attempts' },
              {
                title: 'Создан',
                key: 'created_at',
                render: (_, row: any) => fmtDate(row.created_at),
              },
              {
                title: 'Ошибка',
                key: 'error',
                render: (_, row: any) =>
                  row.error_message ? (
                    <Button
                      size="small"
                      type="link"
                      danger
                      onClick={() => setErrorModal({ open: true, text: row.error_message })}
                    >
                      Показать
                    </Button>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
        </Card>

        <Card title="Опасная зона">
          <Button danger onClick={() => setDeleteModal(true)}>
            Удалить тенант
          </Button>
        </Card>
      </Space>

      <Modal
        open={checkModal.open}
        title={`Проверка: ${checkModal.hostname}`}
        onCancel={() => setCheckModal({ open: false })}
        footer={<Button onClick={() => setCheckModal({ open: false })}>Закрыть</Button>}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <Typography.Text strong>DNS: </Typography.Text>
            <Tag color={checkModal.dnsOk ? 'green' : 'red'}>
              {checkModal.dnsOk ? 'OK' : 'Ошибка'}
            </Tag>
            {checkModal.dnsAddresses?.length > 0 && (
              <Typography.Text type="secondary">
                {' '}
                {checkModal.dnsAddresses.join(', ')}
              </Typography.Text>
            )}
          </div>
          <div>
            <Typography.Text strong>HTTPS: </Typography.Text>
            <Tag color={checkModal.httpsOk ? 'green' : 'red'}>
              {checkModal.httpsOk ? 'OK' : 'Ошибка'}
            </Tag>
            {checkModal.httpsError && (
              <Typography.Text type="danger"> {checkModal.httpsError}</Typography.Text>
            )}
          </div>
          {ssl && (
            <div>
              <Typography.Text strong>SSL: </Typography.Text>
              <Tag color={ssl.color}>{ssl.label}</Tag>
              <div>
                <Typography.Text type="secondary">
                  Subject: {checkModal.tls.subject?.CN}
                </Typography.Text>
              </div>
              <div>
                <Typography.Text type="secondary">
                  Действует до: {checkModal.tls.valid_to}
                </Typography.Text>
              </div>
              {ssl.hint && (
                <div>
                  <Typography.Text type="danger">{ssl.hint}</Typography.Text>
                </div>
              )}
            </div>
          )}
        </Space>
      </Modal>

      <Modal
        open={errorModal.open}
        title="Ошибка"
        onCancel={() => setErrorModal({ open: false, text: '' })}
        footer={<Button onClick={() => setErrorModal({ open: false, text: '' })}>Закрыть</Button>}
      >
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
          {errorModal.text}
        </pre>
      </Modal>

      <Modal
        open={deleteModal}
        title="Удаление тенанта"
        onCancel={() => setDeleteModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setDeleteModal(false)}>
            Отмена
          </Button>,
          <Button key="confirm" danger type="primary" onClick={onDelete}>
            Удалить
          </Button>,
        ]}
      >
        <p>
          Удалить тенант <strong>{tenant?.name}</strong> ({hostname})?
          <br />
          Будут удалены: БД , пользователь БД, все данные, домен из реестра.
        </p>
      </Modal>
    </B2BLayout>
  )
}
